// pricing.js
import React from "react";
import styles from "./pricing.module.css";
import { FaCheck } from "react-icons/fa";
const Pricing = () => {
  const plans = [
    {
      title: "Starter",
      description: "Start crafting better customer experiences",
      monthlyPrice: "$19",
      annualPrice: "$16 per month billed annually",
      annualSaving: "Save 16%",
      cta: "10 Free Trial Calls",
      highlight: false,
      features: [
        "Voice Activity Detection (VAD) - Detects when the customer is speaking or silent to avoid overlapping conversation",
        "Context-aware Dialogue - Maintains conversation context across multiple interactions",
        "Call Event Webhooks - Sends real-time call status updates to backend systems",
      ],
    },
    {
      title: "Growth",
      description: "Intelligent AI agentic actions to scale self-service",
      monthlyPrice: "$49",
      annualPrice: "$41 per month billed annually",
      annualSaving: "Save 16%",
      cta: "10 Free Trial Calls",
      highlight: true,
      features: [
        "Voice Activity Detection (VAD) - Detects when the customer is speaking or silent to avoid overlapping conversation",
        "Barge-in / Interruption Handling - Allows the customer to interrupt the AI at any time for natural interaction",
        "Low-latency Streaming Audio - Ensures near real-time voice responses with minimal delay",
        "Noise Suppression - Filters background noise for clearer customer conversations",
        "Echo Cancellation - Prevents voice echo and feedback during calls",
        "Silence Timeout Handling - Detects long silence and prompts or ends the call appropriately",
        "Context-aware Dialogue - Maintains conversation context across multiple interactions",
        "Intent Detection - Understands customer intent to drive the correct call flow",
        "Sentiment Analysis - Identifies customer sentiment (positive, neutral, negative) during the call",
        "DND / Consent Handling - Ensures calls respect Do-Not-Disturb and consent regulations",
        "Call Event Webhooks - Sends real-time call status updates to backend systems",
      ],
    },
    {
      title: "Enterprise",
      description:
        "Our most powerful AI agents for advanced workflows and flexibility",
      monthlyPrice: "Custom",
      annualPrice: "Custom Pricing",
      annualSaving: "",
      cta: "Contact Sales",
      ctaLink: "/contactus",
      highlight: false,
      features: [
        "Voice Activity Detection (VAD) - Detects when the customer is speaking or silent to avoid overlapping conversation",
        "Barge-in / Interruption Handling - Allows the customer to interrupt the AI at any time for natural interaction",
        "Low-latency Streaming Audio - Ensures near real-time voice responses with minimal delay",
        "Noise Suppression - Filters background noise for clearer customer conversations",
        "Echo Cancellation - Prevents voice echo and feedback during calls",
        "Silence Timeout Handling - Detects long silence and prompts or ends the call appropriately",
        "Diarization - Identifies and separates speaker voices within the call",
        "Context-aware Dialogue - Maintains conversation context across multiple interactions",
        "Intent Detection - Understands customer intent to drive the correct call flow",
        "Fallback Handling - Gracefully handles unclear responses with safe alternatives",
        "Sentiment Analysis - Identifies customer sentiment (positive, neutral, negative) during the call",
        "DND / Consent Handling - Ensures calls respect Do-Not-Disturb and consent regulations",
        "Time-zone Aware Dialing - Places calls only during permissible local business hours",
        "Call Event Webhooks - Sends real-time call status updates to backend systems",
        "PII Masking - Automatically hides sensitive customer information in logs and transcripts",
      ],
    },
  ];

  const [billingCycle, setBillingCycle] = React.useState("annual"); // 'annual' or 'monthly'

  return (
    <div className={styles.pricingContainer}>
      <header className={styles.header}>
        <h1>Simple and Affordable for CallCentry</h1>
        <p className={styles.subtitle}>
          Tailored plans to automate support, boost engagement, and drive sales
          on your website.
        </p>

        {/* Billing Toggle */}
        <div className={styles.billingToggle}>
          <div className={styles.toggleContainer}>
            <span
              className={
                billingCycle === "annual"
                  ? styles.activeLabel
                  : styles.inactiveLabel
              }
            >
              Annual Billing
            </span>
            <div
              className={styles.toggleSwitch}
              onClick={() =>
                setBillingCycle(
                  billingCycle === "annual" ? "monthly" : "annual",
                )
              }
            >
              <div
                className={`${styles.toggleSlider} ${billingCycle === "monthly" ? styles.monthly : ""}`}
              ></div>
            </div>
            <span
              className={
                billingCycle === "monthly"
                  ? styles.activeLabel
                  : styles.inactiveLabel
              }
            >
              Monthly Billing
            </span>
          </div>
          <p className={styles.savingsNote}>
            Save up to 17% with annual billing
          </p>
        </div>
      </header>

      <div className={styles.plansContainer}>
        {plans.map((plan, index) => (
          <div
            key={index}
            className={`${styles.planCard} ${plan.highlight ? styles.highlighted : ""}`}
          >
            {plan.highlight && (
              <div className={styles.popularBadge}>Most Popular</div>
            )}

            <div className={styles.planHeader}>
              <h2 className={styles.planTitle}>{plan.title}</h2>
              <p className={styles.planDescription}>{plan.description}</p>

              <div className={styles.pricingSection}>
                {billingCycle === "annual" ? (
                  <>
                    <div className={styles.annualPrice}>{plan.annualPrice}</div>
                    {plan.annualSaving && (
                      <div className={styles.savingBadge}>
                        {plan.annualSaving}
                      </div>
                    )}
                  </>
                ) : (
                  <div className={styles.monthlyPrice}>
                    {plan.title === "Enterprise"
                      ? plan.monthlyPrice
                      : `${plan.monthlyPrice} billed monthly`}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.planFeatures}>
              <div className={styles.featureItem}></div>
              <div className={styles.featureItem}></div>

              <div className={styles.enterpriseFeatures}>
                {plan.features.map((feature, idx) => (
                  <div key={idx} className={styles.enterpriseFeature}>
                    {feature.includes(":") || feature.includes("and...") ? (
                      <div className={styles.featureHeading}>{feature}</div>
                    ) : feature.includes("plus:") ? (
                      <div className={styles.featureSubheading}>{feature}</div>
                    ) : feature.includes("Salesforce") ? (
                      <>
                        <div className={styles.integrationsTitle}>
                          All integrations in the Advanced plan plus:
                        </div>
                        <div className={styles.integrationItem}>
                          • Salesforce
                        </div>
                        <div className={styles.integrationItem}>
                          • Sharepoint / Onedrive
                        </div>
                        <div className={styles.integrationItem}>
                          • Freshdesk
                        </div>
                        <div className={styles.integrationItem}>• Zoho</div>
                        <div className={styles.integrationItem}>
                          • Confluence
                        </div>
                        <div className={styles.integrationItem}>
                          • Active Campaign
                        </div>
                      </>
                    ) : (
                      <div className={styles.enterpriseFeatureItem}>
                        <FaCheck className={styles.featureIcon} />
                        <span>{feature}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <button
              className={`${styles.ctaButton} ${
                plan.highlight ? styles.highlightedCta : ""
              }`}
              onClick={(e) => {
                if (plan.title === "Enterprise") {
                  e.preventDefault();
                  navigate("/contactus");
                }
              }}
            >
              {plan.cta}
            </button>
          </div>
        ))}
      </div>

      <div className={styles.footerNote}>
        
        <p>
          Need a custom plan?{" "}
          <a href="/contactus" className={styles.contactLink}>
            Contact our sales team
          </a>
        </p>
      </div>
    </div>
  );
};

export default Pricing;
